import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Estoques.module.css';
import EstoqueModal from './EstoqueModal';
import {
  listar,
  criar,
  atualizar,
  COLECOES,
} from '../../../services/firestore';
import { db } from '../../../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  doc,
  serverTimestamp,
} from 'firebase/firestore';

// ====================================================================
// CADASTRO DE ESTOQUES — Bloco 5.1
// Conectado ao Firestore: estoques + usuarios + produtos.
// ====================================================================

// ====================================================================
// HELPERS
// ====================================================================

function corDoAvatar(nome) {
  const cores = [
    { bg: '#f0e6fb', text: '#5B2A8C' },
    { bg: '#FCEBEB', text: '#A32D2D' },
    { bg: '#E1F5EE', text: '#0F6E56' },
    { bg: '#EEEDFE', text: '#534AB7' },
    { bg: '#FFF8E6', text: '#7A5200' },
    { bg: '#E6F1FB', text: '#185FA5' },
  ];
  const hash = (nome.charCodeAt(0) + (nome.charCodeAt(1) || 0)) % cores.length;
  return cores[hash];
}

function primeiroNome(nomeCompleto) {
  return nomeCompleto.split(' ')[0];
}

function normalizarPerfil(perfil) {
  if (!perfil) return '';
  return String(perfil).trim().toUpperCase();
}

function normalizarEstoque(e) {
  return {
    ...e,
    nome: e.nome || '',
    descricao: e.descricao || '',
    responsaveis: Array.isArray(e.responsaveis) ? e.responsaveis : [],
    niveis: Array.isArray(e.niveis) ? e.niveis : [],
    ativo: e.ativo ?? true,
  };
}

// ====================================================================
// COMPONENTE
// ====================================================================

export default function Estoques() {
  const navigate = useNavigate();

  const [estoques, setEstoques] = useState([]);
  const [usuariosDb, setUsuariosDb] = useState([]);
  const [produtosDb, setProdutosDb] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [estoqueEditando, setEstoqueEditando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [estoquesDados, usuariosDados, produtosDados] = await Promise.all([
        listar(COLECOES.ESTOQUES, { ordenarPor: 'nome' }),
        listar(COLECOES.USUARIOS, { ordenarPor: 'nome' }),
        listar(COLECOES.PRODUTOS, { ordenarPor: 'nome' }),
      ]);
      setEstoques(estoquesDados.map(normalizarEstoque));
      // Filtra apenas Gestor e Operador ativos
      setUsuariosDb(
        usuariosDados
          .filter((u) => u.ativo !== false)
          .filter((u) => normalizarPerfil(u.perfil) !== 'VISUALIZADOR')
          .map((u) => ({ ...u, perfil: normalizarPerfil(u.perfil) }))
      );
      setProdutosDb(produtosDados.filter((p) => p.ativo !== false));
    } catch (e) {
      setErro(e.message || 'Erro ao carregar estoques.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const estoquesFiltrados = useMemo(() => {
    return estoques.filter((e) => {
      if (busca && !e.nome.toLowerCase().includes(busca.toLowerCase())) return false;
      if (filtroStatus === 'Ativos' && !e.ativo) return false;
      if (filtroStatus === 'Inativos' && e.ativo) return false;
      return true;
    });
  }, [estoques, busca, filtroStatus]);

  const totalAtivos = estoques.filter((e) => e.ativo).length;
  const totalInativos = estoques.length - totalAtivos;

  function abrirNovo() {
    setEstoqueEditando(null);
    setModalAberto(true);
  }

  function abrirEditar(estoque) {
    setEstoqueEditando(estoque);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setEstoqueEditando(null);
  }

  async function salvar(dados) {
    setSalvando(true);
    setErro('');
    try {
      const limpo = {
        nome: (dados.nome || '').trim(),
        descricao: (dados.descricao || '').trim(),
        responsaveis: Array.isArray(dados.responsaveis) ? dados.responsaveis : [],
        niveis: Array.isArray(dados.niveis) ? dados.niveis : [],
        ativo: dados.ativo ?? true,
      };
      if (estoqueEditando) {
        const nomeAntigo = (estoqueEditando.nome || '').trim();
        const nomeNovo = limpo.nome;
        await atualizar(COLECOES.ESTOQUES, estoqueEditando.id, limpo);
        // Se o nome mudou, propaga para estoque_posicao e estoque_limites
        // (essas colecoes guardam o nome desnormalizado para listagens rapidas)
        if (nomeAntigo !== nomeNovo) {
          await propagarNomeEstoque(estoqueEditando.id, nomeNovo);
        }
      } else {
        await criar(COLECOES.ESTOQUES, limpo);
      }
      fecharModal();
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao salvar o estoque.');
    } finally {
      setSalvando(false);
    }
  }

  // Atualiza nome desnormalizado nas coleções dependentes
  async function propagarNomeEstoque(estoqueId, nomeNovo) {
    const colecoes = ['estoque_posicao', 'estoque_limites'];
    for (const nomeColecao of colecoes) {
      const q = query(
        collection(db, nomeColecao),
        where('estoqueId', '==', estoqueId)
      );
      const snap = await getDocs(q);
      if (snap.empty) continue;
      // Batch de até 500 docs por chamada (limite do Firestore)
      let batch = writeBatch(db);
      let count = 0;
      for (const docSnap of snap.docs) {
        batch.update(doc(db, nomeColecao, docSnap.id), {
          estoqueNome: nomeNovo,
          atualizadoEm: serverTimestamp(),
        });
        count++;
        if (count >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          count = 0;
        }
      }
      if (count > 0) await batch.commit();
    }
  }

  async function toggleAtivo(id) {
    const alvo = estoques.find((e) => e.id === id);
    if (!alvo) return;
    setErro('');
    setEstoques((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ativo: !e.ativo } : e))
    );
    try {
      await atualizar(COLECOES.ESTOQUES, id, { ativo: !alvo.ativo });
    } catch (e) {
      setErro(e.message || 'Erro ao alterar o status do estoque.');
      setEstoques((prev) =>
        prev.map((e) => (e.id === id ? { ...e, ativo: alvo.ativo } : e))
      );
    }
  }

  function nomeUsuario(userId) {
    const u = usuariosDb.find((x) => x.id === userId);
    return u ? (u.nome || u.email || userId) : userId;
  }

  return (
    <>
      <div className={styles.topbar}>
        <div className={styles.pageTitle}>Cadastro de Estoques</div>
        <button className={styles.btnVoltar} onClick={() => navigate('/cadastros')}>
          ← Voltar para Cadastros
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.toolbar}>
        <input
          className={styles.toolbarInput}
          placeholder="🔍 Buscar estoque..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className={styles.toolbarSelect}
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="Ativos">Ativos</option>
          <option value="Inativos">Inativos</option>
        </select>
        <button className={styles.btnNovo} onClick={abrirNovo}>
          + Novo estoque
        </button>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {estoques.length} estoques cadastrados ({totalAtivos} ativos · {totalInativos}{' '}
            {totalInativos === 1 ? 'inativo' : 'inativos'})
          </span>
          <span className={styles.tabelaHint}>Clique em ✏️ para editar</span>
        </div>

        <div className={styles.colHeader}>
          <div>Estoque</div>
          <div>Descrição</div>
          <div>Responsáveis</div>
          <div>Níveis</div>
          <div></div>
        </div>

        {carregando ? (
          <div className={styles.semResultados}>Carregando estoques...</div>
        ) : estoquesFiltrados.length === 0 ? (
          <div className={styles.semResultados}>
            {estoques.length === 0
              ? 'Nenhum estoque cadastrado ainda. Clique em "+ Novo estoque" para começar.'
              : 'Nenhum estoque encontrado com os filtros aplicados.'}
          </div>
        ) : (
          estoquesFiltrados.map((estoque) => (
            <div
              key={estoque.id}
              className={`${styles.row} ${!estoque.ativo ? styles.inativo : ''}`}
            >
              <div>
                <div className={styles.estoqueNome}>
                  {estoque.nome}
                </div>
              </div>
              <div className={styles.estoqueDesc}>
                {estoque.descricao || <span style={{ color: '#bbb' }}>—</span>}
              </div>
              <div className={styles.responsaveis}>
                {estoque.responsaveis.length === 0 ? (
                  <span className={styles.respVazio}>— Nenhum responsável</span>
                ) : (
                  estoque.responsaveis.map((userId) => {
                    const nome = nomeUsuario(userId);
                    const cor = corDoAvatar(nome);
                    return (
                      <span key={userId} className={styles.respChip}>
                        <span
                          className={styles.respAvatar}
                          style={{ background: cor.bg, color: cor.text }}
                        >
                          {nome.charAt(0)}
                        </span>
                        <span className={styles.respNome}>{primeiroNome(nome)}</span>
                      </span>
                    );
                  })
                )}
              </div>
              <div>
                {estoque.niveis.length === 0 ? (
                  <span className={styles.niveisVazio}>sem níveis</span>
                ) : (
                  <span className={styles.niveisInfo}>
                    <strong>{estoque.niveis.length}</strong>{' '}
                    {estoque.niveis.length === 1 ? 'produto' : 'produtos'}
                    <br />
                    com nível
                  </span>
                )}
              </div>
              <div className={styles.acoes}>
                <span
                  className={`${styles.statusTag} ${estoque.ativo ? styles.stAtivo : styles.stInativo}`}
                >
                  {estoque.ativo ? 'Ativo' : 'Inativo'}
                </span>
                <button
                  className={`${styles.btnAcao} ${styles.editar}`}
                  title="Editar"
                  onClick={() => abrirEditar(estoque)}
                >
                  ✏️
                </button>
                <button
                  className={`${styles.btnAcao} ${styles.inativar}`}
                  title={estoque.ativo ? 'Inativar' : 'Reativar'}
                  onClick={() => toggleAtivo(estoque.id)}
                >
                  {estoque.ativo ? '🚫' : '✅'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {modalAberto && (
        <EstoqueModal
          estoque={estoqueEditando}
          usuarios={usuariosDb}
          produtos={produtosDb}
          onSalvar={salvar}
          onCancelar={fecharModal}
          salvando={salvando}
        />
      )}
    </>
  );
}